import { z } from 'zod'
import type { ZodObject, ZodType } from 'zod'

type SchemaField<T extends string> = {
  path: T
  schema: ZodType
  label?: string
}

type SchemaTree = {
  [key: string]: ZodType | SchemaTree
}

export const createMultiDynamicSchema = <T extends string>(
  fields: SchemaField<T>[]
): ZodObject => {
  const intermediateTree: SchemaTree = {}

  for (const field of fields) {
    const keys = field.path.split('.')
    let current = intermediateTree

    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        current[key] = field.schema
      } else {
        current[key] = current[key] || {}
        current = current[key] as SchemaTree
      }
    })
  }

  const compileTree = (tree: SchemaTree): ZodObject => {
    const shape: Record<string, ZodType> = {}

    Object.keys(tree).forEach((key) => {
      const value = tree[key]

      if (value === undefined) return

      if (value instanceof z.ZodType) {
        shape[key] = value
      } else {
        shape[key] = compileTree(value)
      }
    })

    return z.object(shape)
  }

  const finalSchema = compileTree(intermediateTree)

  return z.looseObject(finalSchema.shape)
}

export type TMultiDynamicSchema = ReturnType<typeof createMultiDynamicSchema>

export const requiredString = (fieldName: string, maxLength?: number) => {
  const schema = z.string().min(1, `${fieldName} is required`)

  return maxLength === undefined
    ? schema
    : schema.max(
        maxLength,
        `${fieldName} must be ${maxLength} characters or fewer`
      )
}

export type TRequiredString = ReturnType<typeof requiredString>

export const optionalString = (fieldName: string, maxLength?: number) => {
  const schema =
    maxLength === undefined
      ? z.string()
      : z
          .string()
          .max(
            maxLength,
            `${fieldName} must be ${maxLength} characters or fewer`
          )

  return schema.optional()
}

export type TOptionalString = ReturnType<typeof optionalString>

export const requiredNumber = (fieldName: string, overrideMessage?: string) =>
  z
    .number({ message: overrideMessage || `${fieldName} must be a number` })
    .refine((val) => !isNaN(val), {
      message: overrideMessage || `${fieldName} must be a valid number`,
    })

export type TRequiredNumber = ReturnType<typeof requiredNumber>

export const optionalNumber = (
  fieldName: string,
  overrideMessage?: string,
  options?: { min?: number }
) => {
  const numberSchema = z.coerce.number({
    message: overrideMessage || `${fieldName} must be a number`,
  })

  const boundedSchema =
    options?.min === undefined
      ? numberSchema
      : numberSchema.min(
          options.min,
          `${fieldName} must be ${options.min} or greater`
        )

  return z.preprocess(
    (val) =>
      val === '' || val === null || val === undefined ? undefined : val,
    boundedSchema
      .refine((val) => !Number.isNaN(val), {
        message: overrideMessage || `${fieldName} must be a valid number`,
      })
      .optional()
  )
}

export type TOptionalNumber = ReturnType<typeof optionalNumber>

export interface UrlFactoryOptions {
  protocol?: RegExp
  hostname?: RegExp
  normalize?: boolean
  requiredMessage?: string
  invalidMessage?: string
  defaultProtocol?: string
  optional?: boolean
}

export const createWebUrlSchema = (options: UrlFactoryOptions = {}) => {
  const {
    protocol = /^https?$/,
    hostname = /^([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,6}$/,
    normalize = true,
    requiredMessage = 'This URL field is required',
    invalidMessage = 'Invalid URL format',
    defaultProtocol = 'https',
    optional = false,
  } = options

  const baseSchema = optional
    ? z.string().optional()
    : z.string().min(1, { error: requiredMessage })

  const transformedSchema = baseSchema.transform((val) => {
    if (optional && (!val || val.trim() === '')) {
      return undefined
    }

    const stringVal = val || ''

    if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(stringVal)) {
      return `${defaultProtocol}://${stringVal}`
    }

    return stringVal
  })

  // 4. Build the core validation schema
  const urlSchema = z.url({
    protocol,
    hostname,
    normalize,
    error: invalidMessage,
  })

  // 5. If optional, make the piped target optional so it accepts the `undefined` from step 2
  return optional
    ? transformedSchema.pipe(urlSchema.optional())
    : transformedSchema.pipe(urlSchema)
}

export type TWebUrlSchema = ReturnType<typeof createWebUrlSchema>

export const requiredEmail = z.email('A valid email is required')

export type TRequiredEmail = z.infer<typeof requiredEmail>

export const emailValidationSchema = z.object({
  email: requiredEmail,
})

export type TEmailValidationSchema = z.infer<typeof emailValidationSchema>
